<script setup lang="ts">
import { onMounted, ref, type Ref } from 'vue';
import Column from 'primevue/column';
import { useRoute, useRouter } from 'vue-router';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import { HoZuHu, HoZuHuCollection } from '@/models/HoZuHu';
import KpNumberField from '@/components/KpNumberField.vue';
import { errToToast, reallyCancel } from '@/helpers/tools';
import { useToast } from 'primevue/usetoast';
import type { DataTableRowEditCancelEvent, DataTableRowEditSaveEvent } from 'primevue/datatable';
import { getI18n } from '@/helpers/i18n';

const router = useRouter();
const route = useRoute();
const toast = useToast();

const saving = ref(false);
const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, []);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 100);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const { collection, serverTotal } = useCollection(new HoZuHuCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
});

onMounted(() => {
    if (collection.loading.value !== true) {
        collection.query();
    }
});

const editingRows = ref<HoZuHu[]>([]);
function editRecord(r: HoZuHu) {
    router.push({ name: 'bg_geb_ho_zu_hu_edit', params: { id: r?.id } });
}

async function onSave(event: DataTableRowEditSaveEvent) {
    saving.value = true;
    try {
        event.data.faktor = event.newData.faktor;
        await event.data.save();
        event.data.commit();
        toast.add({
            severity: 'success',
            detail: getI18n().t('datensatz_gespeichert'),
            life: 1000,
        });
    } catch (e) {
        errToToast(e, toast);
    } finally {
        saving.value = false;
    }
}

function onCancel(event: DataTableRowEditCancelEvent) {
    if (reallyCancel(event.data)) {
        event.data.rollback();
    }
}
</script>
<template>
    <div style="height: 100%">
        <KpBaseCollectionGrid
            :collection="collection"
            v-model:table-sort="tableSort"
            v-model:editingRows="editingRows"
            :title="$t('umrechnungsfaktor_ho_zu_hu')"
            scrollable
            scroll-height="flex"
            editMode="row"
            paginator
            v-model:page="page"
            v-model:pageSize="pageSize"
            :showAddBtn="false"
            :showRecordDeleteBtn="false"
            :showRecordEditBtn="false"
            :total-records="serverTotal"
            @editrecord="editRecord($event as HoZuHu)"
            @row-edit-save="onSave"
            @row-edit-cancel="onCancel"
        >
            <Column field="faktor" :header="$t('ho_zu_hu')">
                <template #editor="{ data }">
                    <KpNumberField v-model="data.faktor" :maxFractionDigits="5" class="col-6" fluid></KpNumberField>
                </template>
            </Column>
            <Column :rowEditor="true" style="width: 10%; min-width: 8rem" bodyStyle="text-align:center"></Column>
        </KpBaseCollectionGrid>
    </div>
</template>
